// Per-route meta titles / descriptions.
//
// Keyed by pathname exactly as routed in App.js. Anything not listed here
// falls back to DEFAULT_SEO (the values also hard-coded in public/index.html,
// so a fresh page load and a client-side nav agree).
//
// This file is the only place route meta lives. Page components must not
// render their own <Helmet> title/description/canonical: <Seo /> is mounted
// once in App.js and a second copy in the page would emit a competing
// canonical, which is worse than none at all.
//
// noindex: true  -> <meta name="robots" content="noindex, follow">
// Used for login-required and post-action pages that should stay out of the
// index but still pass link equity onward.

import { helpCategories } from "../data/helpCenter";

export const SITE_URL = "https://zerotyping.ai";

export const DEFAULT_SEO = {
  title: "Zero Typing AI | AI Voice Typing & Dictation Software",
  description:
    "Zero Typing AI turns speech into clean, formatted text in Google Docs, Gmail, Slack, and your favorite AI chat tools. Try AI voice typing free.",
};

export const SEO_BY_PATH = {
  // ---- Public marketing ----------------------------------------------------
  "/pricing": {
    title: "Zero Typing AI Pricing – Plans for Voice Typing",
    description:
      "Compare Zero Typing AI pricing plans and find the right fit for how you write, chat, and work. See features by plan.",
  },
  "/download": {
    title: "Download Zero Typing AI: Voice Typing App",
    description:
      "Download Zero Typing AI and turn your speech into clean, formatted text instantly. Fast, accurate AI voice typing. Get started free today.",
  },
  "/windows": {
    title: "Download Zero Typing for Windows | Voice Typing App",
    description:
      "Turn your voice into flawlessly formatted text on your Windows PC. Zero Typing is AI voice typing that works in any app. Signed installer, quick setup, free to try.",
  },
  "/mac": {
    title: "Zero Typing AI for Mac – Voice Typing App",
    description:
      "Get Zero Typing AI for Mac and dictate clean, formatted text into any app you use. Download free and start today.",
  },
  "/demo": {
    title: "Zero Typing AI Demo – See Voice Typing in Action",
    description:
      "Watch how Zero Typing AI turns speech into clean text inside Docs, Gmail, Slack, and more before you download it.",
  },
  "/voice-typing-for-google-docs": {
    title: "Voice Typing for Google Docs | Zero Typing AI",
    description:
      "Fast, accurate AI voice typing for Google Docs. Speak naturally without punctuation commands and get instant, clean dictation.",
  },
  "/voice-typing-for-slack-and-notion": {
    title: "Voice Typing for Slack and Notion | Dictate Messages Instantly – Zero Typing AI",
    description:
      "Dictate Slack messages and Notion docs hands-free with Zero Typing AI. Voice dictation for Slack and Notion that's 5x faster than typing, no plugin required.",
  },
  "/voice-typing-for-ai-chat-prompts": {
    title: "Speak Prompts to ChatGPT, Claude & Gemini | Zero Typing AI",
    description:
      "Speak prompts to ChatGPT, Claude, and Gemini. Zero Typing AI is voice dictation for AI chat tools, dictate prompts up to 5x faster. Try it free.",
  },
  "/login": {
    title: "Log In to Zero Typing AI",
    description:
      "Log in to your Zero Typing AI account to manage settings, check usage, and keep dictating across your apps.",
    // Not prerendered, served from app-shell.html, and excluded from the
    // sitemap: it ranks for nothing. Marked here so the client-side head agrees
    // with the static one rather than emitting a competing "index" directive.
    noindex: true,
  },
  "/how-it-works": {
    title: "How Zero Typing AI Works – Voice to Text Explained",
    description:
      "See how Zero Typing AI converts speech into clean, formatted text in real time, right inside the apps you already use.",
    // The page is a video and a heading, nothing a crawler can read, and
    // /how-ai-voice-typing-works covers the same topic properly. Kept out of the
    // sitemap and out of prerendering; still reachable by direct link.
    noindex: true,
  },

  // ---- Blog ----------------------------------------------------------------
  "/blogs": {
    title: "Blogs | Zero Typing AI",
    description:
      "Practical guides, product tips, AI insights, and voice typing workflows to help you turn spoken thoughts into clean, ready-to-use text.",
  },
  "/how-ai-voice-typing-works": {
    title: "How AI Voice Typing Actually Works | Zero Typing AI",
    description:
      "AI voice typing is more than speech-to-text. See how your voice is captured, transcribed, and refined into clean, ready-to-use writing with Zero Typing AI.",
  },
  "/voice-prompting-ai": {
    title: "Voice Prompting AI: A Complete Guide | Zero Typing AI",
    description:
      "Voice prompting AI made simple. Learn how to dictate better prompts to ChatGPT, Claude, and Gemini, and why Zero Typing AI beats built-in voice typing.",
  },
  "/voice-typing-for-gmail-and-email": {
    title: "Dictate Emails with AI",
    description:
      "Dictate emails with AI in Gmail. Zero Typing turns your voice into clean, punctuated text. Try it free on Mac, Windows, and Android.",
  },
  "/blog/voice-typing-for-developers": {
    title: "Voice Typing for Developers: Dictate Code Comments & Commits",
    description:
      "Voice coding dictation for developers: learn how to dictate code comments, write clear commit messages, and speak better AI prompts with Zero Typing AI.",
  },
  "/blog/voice-typing-vs-built-in-dictation": {
    title:
      "Voice Typing vs Built-In Dictation: Windows, Mac & Google Docs Compared",
    description:
      "Windows Voice Access, Mac Dictation and Google Docs voice typing compared against AI voice typing. See which one actually saves you time and where each falls short.",
  },
  "/blog/fastest-way-to-write-emails-without-typing": {
    title:
      "Write Emails Without Typing: The Fastest Voice Method | Zero Typing AI",
    description:
      "Learn how to write emails without typing. Use voice dictation for email to draft, polish, and send faster with hands free email writing in Gmail, Outlook, and more.",
  },

  // ---- Legal ---------------------------------------------------------------
  "/privacy-policy": {
    title: "Privacy Policy – Zero Typing AI",
    description:
      "Learn how Zero Typing AI collects, uses, and protects your data when you use our voice typing software.",
  },
  "/terms-of-service": {
    title: "Terms of Service – Zero Typing AI",
    description:
      "Read the terms that govern your use of Zero Typing AI's voice typing and dictation software.",
  },
  "/data-processing-terms": {
    title: "Data Processing Terms – Zero Typing AI",
    description:
      "Review how Zero Typing AI handles and protects customer data as part of our data processing terms.",
  },

  // ---- Help centre ---------------------------------------------------------
  "/help": {
    title: "Help Center – Zero Typing AI Support",
    description:
      "Find answers on setup, features, billing, and troubleshooting for Zero Typing AI, all in one place.",
  },

  // ---- Login-required (noindex, follow) ------------------------------------
  "/app": {
    title: "Zero Typing AI – App",
    description:
      "Your Zero Typing AI workspace for real-time voice typing across your apps.",
    noindex: true,
  },
  "/settings": {
    title: "Account Settings – Zero Typing AI",
    description:
      "Manage your Zero Typing AI preferences, connected apps, and profile details.",
    noindex: true,
  },
  "/usage": {
    title: "Usage – Zero Typing AI",
    description: "Check your Zero Typing AI dictation usage and plan limits.",
    noindex: true,
  },
  "/payment-successful": {
    title: "Payment Successful – Zero Typing AI",
    description:
      "Your payment went through. Your Zero Typing AI plan is now active.",
    noindex: true,
  },

  // ---- Post-action (noindex, follow) ---------------------------------------
  "/signup-successful": {
    title: "Welcome to Zero Typing AI",
    description:
      "You're signed up. Start turning speech into text in Docs, Gmail, Slack, and more.",
    noindex: true,
  },
};


// ---------------------------------------------------------------------------
// Help centre meta, derived from the content itself.
//
// Every category and article already carries a name/title and a summary/
// description in src/data/helpCenter.js. Copying them into the map above would
// mean two sources of truth and a stale <title> the first time someone edits an
// article, so they're generated instead.
// ---------------------------------------------------------------------------
function buildHelpSeo() {
  const entries = {};

  for (const category of helpCategories) {
    entries[`/help/topic/${category.slug}`] = {
      title: `${category.name} – Zero Typing AI Help`,
      description: category.summary,
    };

    for (const article of category.articles || []) {
      entries[`/help/topic/${category.slug}/${article.slug}`] = {
        title: `${article.title} – Zero Typing AI Help`,
        description: article.description,
      };
    }
  }

  return entries;
}

Object.assign(SEO_BY_PATH, buildHelpSeo());

// Trailing slashes and casing shouldn't decide whether a page gets its meta.
export function getSeoForPath(pathname = "/") {
  const normalized =
    pathname.length > 1 ? pathname.replace(/\/+$/, "").toLowerCase() : "/";

  return SEO_BY_PATH[normalized] || DEFAULT_SEO;
}
